import React, { useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaChevronDown } from "react-icons/fa";
import "./Sidebar.css";
import logoImg from "../logo.svg";
import { useAuth } from "../context/AuthContext.tsx";
import { useScreenerUnread } from "../context/ScreenerUnreadContext.tsx";

const PATH_TO_CODE: Record<string, string> = {
  '/screener/momentum-ai':          'MOMENTUM_AI',
  '/screener/postive-gap-up':       'POSITIVE_GAP_UP',
  '/screener/breakout-ai':          'BREAKOUT_AI',
  '/screener/ipo-based-bullish':    'IPO_BASED_BULL_RUN',
  '/screener/vcp':                  'VCP',
  '/screener/bullish-flag-pennant': 'BULLISH_FLAG_PENNANT',
  '/screener/narrow-range':         'NARROW_RANGE',
  '/screener/mountain-scoop':       'MOUNTAIN_SCOOP',
};

const menuItems = [
  {
    name: "AI Screener",
    icon: "🤖",
    path: "/screener/ai",
    pro: false,
    submenu: [
      { name: "Momentum AI", path: "/screener/momentum-ai", pro: true, trending: true },
      { name: "Positive Gap Up", path: "/screener/postive-gap-up", pro: true, trending: true },
      { name: "Breakout AI", path: "/screener/breakout-ai", pro: true }
    ]
  },
  {
    name: "Swing Screener",
    icon: "🔍",
    path: "/screener",
    pro: false,
    submenu: [
      { name: "Ipo Based - Bull Run", path: "/screener/ipo-based-bullish", pro: true },
      { name: "VCP", path: "/screener/vcp", pro: true },
      { name: "Bullish Flag & Pennant", path: "/screener/bullish-flag-pennant", pro: true },
      { name: "Narrow Range", path: "/screener/narrow-range", pro: true },
      { name: "Mountain Scoop", path: "/screener/mountain-scoop", pro: true }
    ]
  },
  // {
  //   name: "Chart Patterns",
  //   icon: "📐",
  //   path: "/chart-patterns",
  //   pro: false,
  //   submenu: [
  //     { name: "Pattern Scanner", path: "/chart-patterns/scanner", pro: true }
  //   ]
  // },
  { name: "Trading Journal", icon: "📒", path: "/pnl" },
  { name: "Swing Calculator", icon: "🔢", path: "/swing-calculator" },
  {
    name: "Tools",
    icon: "🛠️",
    path: "/tools",
    pro: false,
    submenu: [
      { name: "EST Calculator", path: "/tools/est-calculator", pro: false }
    ]
  },
  { name: "Learning Hub", icon: "🎓", path: "/learning-hub" },
  { name: "Updates", icon: "📢", path: "/updates", pro: true },
  {
    name: "Admin Management",
    icon: "⚙️",
    path: "/admin",
    pro: false,
    admin: true,
    submenu: [
      { name: "User Dashboard", path: "/admin/dashboard", admin: true },
      { name: "Add to Screener", path: "/admin/screener-stocks", admin: true },
      { name: "Chart Drawing", path: "/admin/chart-drawing", admin: true },
      { name: "Email Management", path: "/admin/email", admin: true },
      { name: "Updates Management", path: "/admin/updates", admin: true },
      { name: "Trade Journals", path: "/admin/journals", admin: true },
      { name: "Market Screener", path: "/admin/market-screener", admin: true },
      { name: "Chart Patterns", path: "/admin/chart-patterns", admin: true },
      { name: "Plans Management", path: "/admin/plans", admin: true }
    ]
  },
];

const Sidebar = ({ open, toggleSidebar }: any) => {
  const [activeItem, setActiveItem] = useState("Analytics Home");
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { counts, announcementCount } = useScreenerUnread();

  const isAdmin = useMemo(() => {
    if (!isAuthenticated || !user) return false;
    return user.roles?.some(role =>
      role === 'ADMIN' || role === 'ROLE_ADMIN' || role.toUpperCase().includes('ADMIN')
    );
  }, [isAuthenticated, user]);

  const filteredMenuItems = useMemo(() => {
    return menuItems.filter(item => {
      if (item.admin) return isAdmin;
      return true;
    });
  }, [isAdmin]);

  // Total unread for a parent menu (sum of its submenu screeners)
  const getParentUnread = (submenu: any[]) => {
    return submenu.reduce((sum, sub) => {
      const code = PATH_TO_CODE[sub.path];
      return sum + (code ? (counts[code] || 0) : 0);
    }, 0);
  };

  const toggleSubmenu = (itemName: string) => {
    setExpandedMenu(expandedMenu === itemName ? null : itemName);
  };

  return (
    <>
      {open && <div className="overlay" onClick={() => toggleSidebar(false)} />}

      <div className={`sidebar ${open ? "open" : ""}`}>
        <ul className="menu-list">
          {filteredMenuItems.map((item: any) => {
            const parentUnread = item.submenu ? getParentUnread(item.submenu) : 0;

            return (
              <li key={item.name} className="menu-item-wrapper">
                <div
                  className={`menu-item ${location.pathname === "/" ? location.pathname === item.path ? 'active-link' : '' : item.path.length > 1 && location.pathname.startsWith(item.path) ? 'active-link' : ''}`}
                >
                  {item.submenu ? (
                    <div
                      className="menu-link-with-submenu"
                      onClick={() => toggleSubmenu(item.name)}
                    >
                      <div className="menu-link-content">
                        <span className="icon">{item.icon}</span>
                        <span className="title">{item.name}</span>
                        {item.pro && <span className="pro-badge">Pro</span>}
                        {parentUnread > 0 && expandedMenu !== item.name && (
                          <span className="unread-badge">{parentUnread > 99 ? '99+' : parentUnread}</span>
                        )}
                      </div>
                      <span className={`submenu-arrow ${expandedMenu === item.name ? 'expanded' : ''}`}>
                        <FaChevronDown />
                      </span>
                    </div>
                  ) : (
                    <Link to={item.path} onClick={() => toggleSidebar(false)}>
                      <span className="icon">{item.icon}</span>
                      <span className="title">{item.name}</span>
                      {item.pro && <span className="pro-badge">Pro</span>}
                      {item.path === '/updates' && announcementCount > 0 && (
                        <span className="unread-badge">{announcementCount > 99 ? '99+' : announcementCount}</span>
                      )}
                    </Link>
                  )}
                </div>

                {/* Submenu */}
                {item.submenu && (
                  <ul className={`submenu ${expandedMenu === item.name ? 'expanded' : ''}`}>
                    {item.submenu.map((subItem: any) => {
                      const code = PATH_TO_CODE[subItem.path];
                      const unread = code ? (counts[code] || 0) : 0;

                      return (
                        <li key={subItem.name} className={`submenu-item ${location.pathname === subItem.path ? 'active-link' : ''}`}>
                          <Link to={subItem.path} onClick={() => toggleSidebar(false)}>
                            <span className="submenu-title">{subItem.name}</span>
                            {subItem.trending && <span className="trending-icon">🔥</span>}
                            {unread > 0 && (
                              <span className="unread-badge">{unread > 99 ? '99+' : unread}</span>
                            )}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
};

export default Sidebar;
